import React from 'react';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import InputField from 'flow/components/ui-components/InputField';
import LobbyButtonPrimaryAction from './LobbyButtonPrimaryAction';
import { ViewerMeetingRole } from 'flow/hooks/useViewerMeetingRole';

interface Props {
    /** Lets the caller size this within a wider row — see MeetingLobby. */
    className?: string;
    name: string;
    onChangeName: (name: string) => void;
    onJoinMeeting: () => void;
    /** Whether to ask for a name at all — see the placeholder note below. */
    showNameInput: boolean;
    /** Asked once by MeetingLobby and handed down — see useViewerMeetingRole. */
    viewerMeetingRole: ViewerMeetingRole;
}

/**
 * The row you act on to get into the meeting: who you are, and the button that
 * takes you in (VID-76 — which may mean asking rather than joining; see
 * LobbyButtonPrimaryAction).
 *
 * Someone signed in whose name the app already knows is not asked for it again,
 * but the row keeps its shape: the empty flex child holds the button on the end
 * side rather than letting it slide across, so the lobby does not rearrange
 * itself between a guest and a colleague.
 *
 * Enter submits, since a name field with a button beside it invites it.
 */
const MeetingLobbyNameEntry = ({
    className,
    name,
    onChangeName,
    onJoinMeeting,
    showNameInput,
    viewerMeetingRole,
}: Props) => {
    const { i18next } = useAppContext();

    return (
        <div className={twMerge('flex items-center gap-3', className)}>
            {showNameInput ? (
                // The wrapper carries the width, not the input: InputField puts
                // className on the <input> itself, two levels down, so flex-1
                // there never reaches the flex item and the button overflows.
                <div className="min-w-0 flex-1">
                    <InputField
                        className="h-10 rounded-2xl text-sm"
                        placeholder={i18next.t(
                            'videoconference.lobby.name_placeholder',
                        )}
                        value={name}
                        onChange={(event) => {
                            event.preventDefault();
                            onChangeName(event.currentTarget.value);
                        }}
                        onKeyUp={(event) => {
                            event.preventDefault();
                            if (event.key === 'Enter' && name) {
                                onJoinMeeting();
                            }
                        }}
                        autoFocus={true}
                    />
                </div>
            ) : (
                <div className="flex-1" />
            )}

            <LobbyButtonPrimaryAction
                name={name}
                onJoinMeeting={onJoinMeeting}
                viewerMeetingRole={viewerMeetingRole}
            />
        </div>
    );
};

export default MeetingLobbyNameEntry;
